import React from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { RichText } from "@hats-and-ladders/client-common";
import LoginBackground from "@/assets/background/login-background.svg";
import type { CMSBanner } from "@/types/cms-types";

interface FullScreenMaintenanceProps {
  banner: CMSBanner;
}

// Maintenance windows are announced in ET regardless of where the user is.
const dateTimeFormat = new Intl.DateTimeFormat("en-US", {
  weekday: "long",
  month: "long",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
  timeZoneName: "short",
  timeZone: "America/New_York",
});

const formatDateTime = (value: string | null): string | null => {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return dateTimeFormat.format(date);
};

interface WindowRowProps {
  label: string;
  value: string;
  dotColor: string;
}

const WindowRow = ({ label, value, dotColor }: WindowRowProps) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
    <Box
      sx={{
        mt: 1,
        width: 8,
        height: 8,
        borderRadius: "50%",
        bgcolor: dotColor,
        flexShrink: 0,
      }}
    />
    <Box>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

/**
 * Full-page maintenance takeover for banners flagged `fullScreenMaintenance`.
 * The heading is fixed copy; the rest comes from the CMS banner — rich-text
 * description as the body, and start/end dates as the maintenance window.
 *
 * `MaintenanceGate` renders this *instead of* the router, so there is no page
 * behind it: no overlay positioning, z-index or scroll lock is needed.
 */
export function FullScreenMaintenance({ banner }: FullScreenMaintenanceProps) {
  const windowRows = [
    {
      label: "Starts",
      value: formatDateTime(banner.startDate),
      dotColor: "primary.main",
    },
    {
      label: "Ends",
      value: formatDateTime(banner.endDate),
      dotColor: "success.main",
    },
  ].filter((row): row is WindowRowProps => !!row.value);

  return (
    <Box
      aria-label={banner.title}
      data-label="FullScreenMaintenance"
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "background.default",
        backgroundImage: `url(${LoginBackground})`,
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
      }}
    >
      <Box
        component="main"
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 3,
          py: 8,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 512 }}>
          {banner.image?.url && (
            <Box
              component="img"
              src={banner.image.url}
              alt={banner.image.alt || ""}
              sx={{ maxWidth: 160, mb: 4, display: "block" }}
            />
          )}

          <Typography
            variant="h2"
            sx={{
              color: "secondary.main",
              letterSpacing: "-0.02em",
              mb: 2,
            }}
          >
            We'll be back soon.
          </Typography>

          <Box
            sx={{
              color: "text.secondary",
              mb: 5,
              "& p": { margin: "0.5rem 0" },
            }}
          >
            <RichText value={banner.description} />
          </Box>

          {windowRows.length > 0 && (
            <Box
              data-label="MaintenanceWindow"
              sx={{
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "background.paper",
                p: 3,
              }}
            >
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  color: "text.secondary",
                  fontFamily: "Nunito",
                  mb: 2,
                }}
              >
                Maintenance Window
              </Typography>

              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {windowRows.map((row) => (
                  <WindowRow key={row.label} {...row} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      <Box component="footer" sx={{ px: 4, py: 3 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          &copy; {new Date().getFullYear()} Hats &amp; Ladders. All rights
          reserved.
        </Typography>
      </Box>
    </Box>
  );
}
